import { useState } from "react";
import { Button, Typography } from "@mui/material";
import {
    Folder,
    Upload,
    Cloud,
    LayoutGrid,
    FileText,
    Link as LinkIcon,
    Landmark,
} from "lucide-react";
import type { SourceFile, SourceFolder, SourceNode } from "./sources-types";
import {
    optionButtonSx,
    selectedOptionSx,
    formPanelClass,
} from "./add-source/addSourceStyles";
import {
    CreateFolderForm,
    UploadFileForm,
    DataRepoForm,
    QuickNoteForm,
    AddLinkForm,
    CreateTableForm,
    ConnectDataBankForm,
} from "./add-source";

interface AddSourceOverlayContentProps {
    parentId: string | null;
    parentLabel: string;
    editNode: SourceNode | null;
    onClose: () => void;
}

type SourceOption =
    | { kind: "folder" }
    | { kind: "file"; type: "file" }
    | { kind: "stub"; type: SourceFile["type"]; label: string };

const OPTIONS: {
    label: string;
    icon: React.ReactNode;
    action: SourceOption;
}[] = [
    {
        label: "Create Folder",
        icon: <Folder size={20} className="text-gray-700" />,
        action: { kind: "folder" },
    },
    {
        label: "Upload File",
        icon: <Upload size={20} className="text-gray-700" />,
        action: { kind: "file", type: "file" },
    },
    {
        label: "Connect Data Repo",
        icon: <Cloud size={20} className="text-gray-700" />,
        action: { kind: "stub", type: "dataRepo", label: "Data repo" },
    },
    {
        label: "Connect Data Bank",
        icon: <Landmark size={20} className="text-gray-700" />,
        action: { kind: "stub", type: "dataBank", label: "Data bank" },
    },
    {
        label: "Create Table",
        icon: <LayoutGrid size={20} className="text-gray-700" />,
        action: { kind: "stub", type: "table", label: "New table" },
    },
    {
        label: "Create Quick Note",
        icon: <FileText size={20} className="text-gray-700" />,
        action: { kind: "stub", type: "quickNote", label: "Quick note" },
    },
    {
        label: "Add Link",
        icon: <LinkIcon size={20} className="text-gray-700" />,
        action: { kind: "stub", type: "link", label: "New link" },
    },
];

const OPTION_INDEX_TABLE = 4;

export default function AddSourceOverlayContent({
    parentId,
    parentLabel,
    editNode,
    onClose,
}: AddSourceOverlayContentProps) {
    const [selectedOptionIndex, setSelectedOptionIndex] = useState<
        number | null
    >(null);
    const [tableDialogOpen, setTableDialogOpen] = useState(false);

    const isEditMode = editNode != null;
    const clearSelection = () => setSelectedOptionIndex(null);

    const handleSuccess = () => {
        clearSelection();
        onClose();
    };

    const handleOptionClick = (idx: number) => {
        if (idx === OPTION_INDEX_TABLE) {
            setTableDialogOpen(true);
            return;
        }
        setSelectedOptionIndex(idx);
    };

    const opt =
        selectedOptionIndex != null ? OPTIONS[selectedOptionIndex] : null;
    const showInlinePanel =
        !isEditMode &&
        selectedOptionIndex != null &&
        selectedOptionIndex !== OPTION_INDEX_TABLE;

    const renderForm = () => {
        const onCancel = isEditMode ? onClose : clearSelection;
        const commonProps = { parentId, onSuccess: handleSuccess, onCancel };

        if (isEditMode && editNode) {
            if (editNode.type === "folder")
                return (
                    <CreateFolderForm
                        {...commonProps}
                        editNode={editNode as SourceFolder}
                    />
                );
            if (editNode.type === "file")
                return (
                    <UploadFileForm
                        {...commonProps}
                        editNode={editNode as SourceFile}
                    />
                );
            if (editNode.type === "dataRepo")
                return (
                    <DataRepoForm
                        {...commonProps}
                        editNode={editNode as SourceFile}
                    />
                );
            if (editNode.type === "dataBank")
                return (
                    <ConnectDataBankForm
                        {...commonProps}
                        editNode={editNode as SourceFile}
                    />
                );
            if (editNode.type === "quickNote")
                return (
                    <QuickNoteForm
                        {...commonProps}
                        editNode={editNode as SourceFile}
                    />
                );
            if (editNode.type === "link")
                return (
                    <AddLinkForm
                        {...commonProps}
                        editNode={editNode as SourceFile}
                    />
                );
            if (editNode.type === "table")
                return (
                    <CreateTableForm
                        parentId={parentId}
                        open={true}
                        onClose={onClose}
                        onSuccess={handleSuccess}
                        editNode={editNode as SourceFile}
                    />
                );
            return null;
        }

        if (!opt) return null;
        if (opt.action.kind === "folder")
            return <CreateFolderForm {...commonProps} />;
        if (opt.action.kind === "file")
            return <UploadFileForm {...commonProps} />;
        if (opt.action.kind === "stub") {
            const { type } = opt.action;
            if (type === "quickNote") return <QuickNoteForm {...commonProps} />;
            if (type === "link") return <AddLinkForm {...commonProps} />;
            if (type === "dataRepo") return <DataRepoForm {...commonProps} />;
            if (type === "dataBank") return <ConnectDataBankForm {...commonProps} />;
        }
        return null;
    };

    return (
        <div className="flex flex-col gap-4">
            <Typography variant="body2" color="text.secondary" className="mb-1">
                {isEditMode ? "Editing: " : "Adding to: "}
                <span className="font-medium text-gray-800">{parentLabel}</span>
            </Typography>

            {!isEditMode && (
                <div className="grid grid-cols-3 gap-4">
                    {OPTIONS.map((option, idx) => (
                        <Button
                            key={idx}
                            fullWidth
                            variant="text"
                            startIcon={
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100">
                                    {option.icon}
                                </div>
                            }
                            onClick={() => handleOptionClick(idx)}
                            sx={{
                                ...optionButtonSx,
                                ...(selectedOptionIndex === idx
                                    ? selectedOptionSx
                                    : {}),
                            }}
                            className="!justify-start !rounded-xl !border !border-gray-200 !px-3 !py-3 transition-all duration-150 hover:!border-gray-300 hover:!shadow-sm"
                        >
                            <span className="text-sm font-medium text-gray-800">
                                {option.label}
                            </span>
                        </Button>
                    ))}
                </div>
            )}

            {isEditMode && <div className={formPanelClass}>{renderForm()}</div>}
            {showInlinePanel && (
                <div className={formPanelClass}>{renderForm()}</div>
            )}

            {!isEditMode && (
                <CreateTableForm
                    parentId={parentId}
                    open={tableDialogOpen}
                    onClose={() => setTableDialogOpen(false)}
                    onSuccess={handleSuccess}
                />
            )}
        </div>
    );
}
